import { calculateAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { buildVehicleOwnerOptions } from "./vehicleOwnerOptions";
import {
  buildTemplateUsers,
  getTemplateUserValues,
  type TemplateUser,
} from "./templateUsers";
import { templateDictionaryItems } from "@/lib/data/static/templateDictionaryItems";
import type { Firm, FirmOffice } from "@/lib/types/Firm";
import type {
  Matter,
  MatterContactConnection,
  MatterIncidentReport,
  MatterInsurance,
  MatterVehicle,
} from "@/lib/types/Matter";
import type { Connection, Contact } from "@/lib/types";
import {
  getInsuranceContactConnection,
  getInsuranceContactConnections,
} from "@/lib/utils/connections/insurance-contact-connections";
import { getIncidentReportContactConnections } from "@/lib/utils/connections/incident-report-contact-connections";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import {
  getMedicalTreatmentContactConnection,
  getMedicalTreatmentContactConnections,
} from "@/lib/utils/connections/medical-treatment-contact-connections";
import {
  formatAddress,
  formatAddressLine1,
  formatAddressLine2,
  formatContactsName,
  formatDate,
  formatLocalOrIntlPhoneNumber,
  formatTime,
  formatVehicleTitle,
} from "@/lib/utils/format";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  calculateNetSettlementAmount,
  calculateNetToClient,
  calculateSettlementTotals,
} from "@/lib/utils/matters/settlement-utils";
import dayjs from "dayjs";
import { getTreatmentAppointmentStartsAt } from "@/lib/utils/matters/medical-treatment-appointment-dates";
import { startCase, uniqBy } from "lodash";

export interface TemplateDictionaryOption {
  vehicleId?: string;
  group?: string;
  id?: string;
  value: string;
  label: string;
  context: string;
}

export type TemplateDictionaryOptionsMap = Record<
  string,
  TemplateDictionaryOption[] | undefined
>;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const multilineSeparator = "\r\n";

const formatShortDate = (value?: string) =>
  value && dayjs(value).isValid() ? dayjs(value).format("MM/DD/YYYY") : "";

export type TemplateContact = Omit<
  Contact,
  "addresses" | "emails" | "faxes" | "phones"
> &
  Partial<Pick<Contact, "addresses" | "emails" | "faxes" | "phones">>;

export type ContactConnectionWithContact = Pick<
  MatterContactConnection,
  "contactId" | "id" | "label"
> & {
  contact: TemplateContact;
  medicalTreatmentId?: string | null;
};

type LoadedContactConnection = ContactConnectionWithContact;

export type InsuranceConnectionWithContact = Omit<Connection, "contact"> & {
  contact: Contact;
};

export const buildIncidentReportSourceConnections = (
  reports: MatterIncidentReport[],
  contacts: Contact[],
): ContactConnectionWithContact[] =>
  reports.flatMap((report) =>
    getIncidentReportContactConnections(
      report,
      "Incident Report Source",
    ).flatMap((connection) => {
      const contact = contacts.find(
        (candidate) => candidate.id === connection.targetObjectId,
      );
      return contact
        ? [
            {
              contact,
              contactId: contact.id,
              id: connection.id,
              label: "Incident report source",
            },
          ]
        : [];
    }),
  );

export const buildMedicalTreatmentProviderConnections = (
  matter: Pick<Matter, "medicalTreatments">,
  contacts: Contact[],
): ContactConnectionWithContact[] =>
  matter.medicalTreatments.flatMap((treatment) =>
    getMedicalTreatmentContactConnections(treatment, "Provider").flatMap(
      (connection) => {
        const contact = contacts.find(
          (candidate) => candidate.id === connection.targetObjectId,
        );
        return contact
          ? [
              {
                contact,
                contactId: connection.targetObjectId,
                id: connection.id,
                label: "Provider",
                medicalTreatmentId: treatment.id,
              },
            ]
          : [];
      },
    ),
  );

export const getMedicalTreatmentProviderContactIds = (
  matter: Pick<Matter, "medicalTreatments">,
): string[] =>
  matter.medicalTreatments.flatMap((treatment) =>
    getMedicalTreatmentContactConnections(treatment, "Provider").map(
      (connection) => connection.targetObjectId,
    ),
  );

export type InsuranceWithConnections = MatterInsurance & {
  insured?: InsuranceConnectionWithContact;
  insurer?: InsuranceConnectionWithContact;
  policyHolder?: InsuranceConnectionWithContact;
  bodilyInjuryAdjuster?: InsuranceConnectionWithContact;
  propertyDamageAdjuster?: InsuranceConnectionWithContact;
  medPayAdjuster?: InsuranceConnectionWithContact;
  adjusterSupervisor?: InsuranceConnectionWithContact;
  subrogationVendor?: InsuranceConnectionWithContact;
  subrogationRepresentative?: InsuranceConnectionWithContact;
  subrogationFileNumber?: string;
  subrogation?: boolean;
  policyLimits?: string;
};

export const buildInsuranceWithConnections = (
  insurance: MatterInsurance,
  contacts: Contact[],
  policyLimits?: string,
): InsuranceWithConnections => {
  const hydratedConnections = insurance.connections.flatMap((connection) => {
    const contact = contacts.find(
      (candidate) => candidate.id === connection.targetObjectId,
    );
    return contact ? [{ ...connection, contact }] : [];
  });
  const getRole = (
    role: Parameters<typeof getInsuranceContactConnection>[1],
  ) => {
    const connection = getInsuranceContactConnection(insurance, role);
    return connection
      ? hydratedConnections.find((candidate) => candidate.id === connection.id)
      : undefined;
  };

  return {
    ...insurance,
    insured: getRole("Insured"),
    insurer: getRole("Insurer"),
    policyHolder: getRole("Policy Holder"),
    bodilyInjuryAdjuster: getRole("Bodily Injury Adjuster"),
    propertyDamageAdjuster: getRole("Property Damage Adjuster"),
    medPayAdjuster: getRole("MedPay Adjuster"),
    adjusterSupervisor: getRole("Adjuster Supervisor"),
    subrogationVendor: getRole("Subrogation Vendor"),
    subrogationRepresentative: getRole("Subrogation Representative"),
    policyLimits,
  };
};

type MatterLienV2 = {
  category?: string | null;
  goalAmount?: number | string | null;
  negotiatedAmount?: number | string | null;
  originalAmount?: number | string | null;
  paidAmount?: number | string | null;
  type?: string | null;
};

const hasContact = (
  connection: ContactConnectionWithContact,
): connection is LoadedContactConnection => Boolean(connection.contact);

const getContactCollection = <T>(items: T[] | null | undefined): T[] =>
  items ?? [];

const getConnectionAddresses = (connection: LoadedContactConnection) =>
  getContactCollection(connection.contact.addresses);

const getContactRoleKey = (connection: ContactConnectionWithContact) =>
  JSON.stringify([connection.contactId, connection.label]);

const getContactOptionId = (
  connection: ContactConnectionWithContact,
  field: string,
  channelId?: string,
) =>
  JSON.stringify([
    "contact",
    connection.contactId,
    connection.label,
    field,
    channelId,
  ]);

const getInsuranceOptionId = (
  insuranceId: string,
  field: string,
  role?: string,
  channelId?: string,
) => JSON.stringify(["insurance", insuranceId, role, field, channelId]);

interface BuildTemplateDictionaryOptionsParams {
  matter: Matter;
  firm?: Pick<Firm, "name">;
  firmOffices?: FirmOffice[];
  templateUsers?: TemplateUser[];
  contacts: ContactConnectionWithContact[];
  clientInsurance: InsuranceWithConnections[];
  defendantInsurance: InsuranceWithConnections[];
  vehicleOwnerContacts?: Contact[];
  clientVehicles: MatterVehicle[];
  defendantVehicles: MatterVehicle[];
}

const INSURANCE_CONTACT_ROLES = [
  "BodilyInjuryAdjuster",
  "PropertyDamageAdjuster",
  "MedPayAdjuster",
  "AdjusterSupervisor",
  "Insured",
  "Insurer",
  "PolicyHolder",
  "SubrogationVendor",
] as const;

type InsuranceContactRole = (typeof INSURANCE_CONTACT_ROLES)[number];
type InsuranceVariablePrefix = "clientInsurance" | "defendantInsurance";

const INSURANCE_ROLE_PROPERTY_MAP: Record<
  InsuranceContactRole,
  keyof InsuranceWithConnections
> = {
  Insured: "insured",
  PolicyHolder: "policyHolder",
  Insurer: "insurer",
  BodilyInjuryAdjuster: "bodilyInjuryAdjuster",
  PropertyDamageAdjuster: "propertyDamageAdjuster",
  MedPayAdjuster: "medPayAdjuster",
  AdjusterSupervisor: "adjusterSupervisor",
  SubrogationVendor: "subrogationVendor",
};

interface InsuranceContactVariableMetadata {
  variable: string;
  prefix: InsuranceVariablePrefix;
  contactRole: InsuranceContactRole;
  field: string;
}

const insuranceContactVariableMetadata: InsuranceContactVariableMetadata[] =
  (() => {
    const metadataByVariable: Record<string, InsuranceContactVariableMetadata> =
      {};
    templateDictionaryItems
      .filter(
        (item) =>
          item.parent === "clientInsurance" ||
          item.parent === "defendantInsurance",
      )
      .forEach((item) => {
        const metadata = parseInsuranceVariable(item.variable);
        if (metadata?.contactRole) {
          metadataByVariable[item.variable] = metadata;
        }
      });
    return Object.values(metadataByVariable);
  })();

function parseInsuranceVariable(
  variable: string,
): InsuranceContactVariableMetadata | undefined {
  const prefix = variable.startsWith("clientInsurance")
    ? "clientInsurance"
    : variable.startsWith("defendantInsurance")
      ? "defendantInsurance"
      : undefined;
  if (!prefix) return undefined;

  const suffix = variable.slice(prefix.length);
  const contactRole = INSURANCE_CONTACT_ROLES.find((role) =>
    suffix.startsWith(role),
  );
  if (!contactRole) return undefined;

  const field = suffix.slice(contactRole.length) || "Name";
  return {
    variable,
    prefix,
    contactRole,
    field,
  };
}

export const buildTemplateDictionaryOptions = ({
  matter,
  firm,
  firmOffices,
  templateUsers = buildTemplateUsers(matter),
  contacts,
  clientInsurance,
  defendantInsurance,
  clientVehicles,
  defendantVehicles,
  vehicleOwnerContacts = [],
}: BuildTemplateDictionaryOptionsParams): TemplateDictionaryOptionsMap => {
  const userOptions: TemplateDictionaryOptionsMap = {};
  templateUsers.forEach(({ isMatterUser, user, roles }) => {
    const values = getTemplateUserValues(user);
    const context = roles.length ? roles.join(", ") : "Firm user";
    Object.entries(values).forEach(([key, value]) => {
      if (!value.trim()) return;
      (userOptions[key] ??= []).push({
        group: isMatterUser ? "Assigned to matter" : "Other firm users",
        id: user.id,
        value,
        label: value,
        context:
          key === "userName" ? context : `${values.userName} - ${context}`,
      });
    });
  });
  const contactsWithContact = contacts.filter(hasContact);
  const incidentAddress = formatAddress({
    street1: matter.incidentAddressStreet1,
    street2: matter.incidentAddressStreet2,
    city: matter.incidentAddressCity,
    state: matter.incidentAddressState ?? "",
    zipCode: matter.incidentAddressZip,
  });
  const allDefendants = uniqBy(
    contacts.filter((connection) => connection.label === "Defendant"),
    (connection) =>
      (connection.contactId || formatContactsName(connection.contact)) ?? "",
  )
    .map((connection) => formatContactsName(connection.contact) ?? "")
    .filter((name): name is string => Boolean(name.trim()))
    .join(", ");
  const allHealthProviders = uniqBy(
    matter.medicalTreatments
      .map((treatment) => {
        const providerConnection = getMedicalTreatmentContactConnection(
          treatment,
          "Provider",
        );

        return (
          getConnectionContactName(providerConnection?.contact) ||
          treatment.description?.trim() ||
          ""
        );
      })
      .filter((name): name is string => Boolean(name.trim())),
    (name) => name,
  ).join(", ");
  const allProvidersDates = Array.from(
    matter.medicalTreatments.reduce((acc, treatment) => {
      const providerConnection = getMedicalTreatmentContactConnection(
        treatment,
        "Provider",
      );

      const providerName = (
        getConnectionContactName(providerConnection?.contact) ||
        treatment.description?.trim() ||
        ""
      ).trim();
      if (!providerName) {
        return acc;
      }

      const appointmentDates = matter.medicalTreatmentAppointments
        .filter(
          (appointment) =>
            appointment.medicalTreatmentId === treatment.id &&
            appointment.outcome !== "missed" &&
            appointment.outcome !== "scheduled" &&
            !getTreatmentAppointmentStartsAt(appointment).isAfter(dayjs()) &&
            Boolean(appointment.date),
        )
        .map((appointment) => appointment.date)
        .filter((date): date is string =>
          Boolean(date && dayjs(date).isValid()),
        );

      if (!appointmentDates.length) {
        return acc;
      }

      const existing = acc.get(providerName) ?? {
        first: appointmentDates[0],
        last: appointmentDates[0],
      };
      const next = appointmentDates.reduce(
        (range, date) => ({
          first: dayjs(date).isBefore(dayjs(range.first)) ? date : range.first,
          last: dayjs(date).isAfter(dayjs(range.last)) ? date : range.last,
        }),
        existing,
      );

      acc.set(providerName, next);
      return acc;
    }, new Map<string, { first: string; last: string }>()),
  )
    .map(
      ([providerName, range]) =>
        `${providerName} ${formatShortDate(range.first)} - ${formatShortDate(range.last)}`,
    )
    .join(multilineSeparator);
  const allProvidersFinal = Array.from(
    matter.medicalTreatments.reduce((acc, treatment) => {
      const providerConnection = getMedicalTreatmentContactConnection(
        treatment,
        "Provider",
      );

      const providerName = (
        getConnectionContactName(providerConnection?.contact) ||
        treatment.description?.trim() ||
        ""
      ).trim();
      if (!providerName) {
        return acc;
      }

      const finalBillAmount = matter.medicalTreatmentBills
        .filter((bill) => bill.medicalTreatmentId === treatment.id)
        .reduce(
          (total, bill) =>
            total +
            Number(
              bill.negotiatedAmount ??
                bill.adjustedBill ??
                bill.originalBill ??
                0,
            ),
          0,
        );

      acc.set(providerName, (acc.get(providerName) ?? 0) + finalBillAmount);
      return acc;
    }, new Map<string, number>()),
  )
    .map(
      ([providerName, total]) =>
        `${providerName} ${currencyFormatter.format(total)}`,
    )
    .join(multilineSeparator);
  const allProvidersOriginal = Array.from(
    matter.medicalTreatments.reduce((acc, treatment) => {
      const providerConnection = getMedicalTreatmentContactConnection(
        treatment,
        "Provider",
      );

      const providerName = (
        getConnectionContactName(providerConnection?.contact) ||
        treatment.description?.trim() ||
        ""
      ).trim();
      if (!providerName) {
        return acc;
      }

      const originalBillAmount = matter.medicalTreatmentBills
        .filter((bill) => bill.medicalTreatmentId === treatment.id)
        .reduce((total, bill) => total + Number(bill.originalBill ?? 0), 0);

      acc.set(providerName, (acc.get(providerName) ?? 0) + originalBillAmount);
      return acc;
    }, new Map<string, number>()),
  )
    .map(
      ([providerName, total]) =>
        `${providerName} ${currencyFormatter.format(total)}`,
    )
    .join(multilineSeparator);
  const allProvidersOriginalFinal = Array.from(
    matter.medicalTreatments.reduce(
      (acc, treatment) => {
        const providerConnection = getMedicalTreatmentContactConnection(
          treatment,
          "Provider",
        );

        const providerName = (
          getConnectionContactName(providerConnection?.contact) ||
          treatment.description?.trim() ||
          ""
        ).trim();
        if (!providerName) {
          return acc;
        }

        const bills = matter.medicalTreatmentBills.filter(
          (bill) => bill.medicalTreatmentId === treatment.id,
        );

        const existing = acc.get(providerName) ?? {
          original: 0,
          final: 0,
        };

        const totals = bills.reduce(
          (billAcc, bill) => ({
            original: billAcc.original + Number(bill.originalBill ?? 0),
            final:
              billAcc.final +
              Number(
                bill.negotiatedAmount ??
                  bill.adjustedBill ??
                  bill.originalBill ??
                  0,
              ),
          }),
          existing,
        );

        acc.set(providerName, totals);
        return acc;
      },
      new Map<
        string,
        {
          original: number;
          final: number;
        }
      >(),
    ),
  )
    .map(
      ([providerName, totals]) =>
        `${providerName} - Original: ${currencyFormatter.format(
          totals.original,
        )} Final: ${currencyFormatter.format(totals.final)}`,
    )
    .join(multilineSeparator);
  const allProvidersOrigFinalAdjustedInsPaid = Array.from(
    matter.medicalTreatments.reduce(
      (acc, treatment) => {
        const providerConnection = getMedicalTreatmentContactConnection(
          treatment,
          "Provider",
        );
        const providerName =
          getConnectionContactName(providerConnection?.contact) ||
          treatment.description?.trim() ||
          "";

        const trimmedProviderName = providerName.trim();
        if (!trimmedProviderName) {
          return acc;
        }

        const bills = matter.medicalTreatmentBills.filter(
          (bill) => bill.medicalTreatmentId === treatment.id,
        );

        const existing = acc.get(trimmedProviderName) ?? {
          original: 0,
          final: 0,
          adjusted: 0,
          insurance: 0,
        };

        const totals = bills.reduce(
          (billAcc, bill) => ({
            original: billAcc.original + Number(bill.originalBill ?? 0),
            final:
              billAcc.final +
              Number(
                bill.negotiatedAmount ??
                  bill.adjustedBill ??
                  bill.originalBill ??
                  0,
              ),
            adjusted: billAcc.adjusted + Number(bill.adjustedBill ?? 0),
            insurance: billAcc.insurance + Number(bill.insurancePaid ?? 0),
          }),
          existing,
        );

        acc.set(trimmedProviderName, totals);
        return acc;
      },
      new Map<
        string,
        {
          original: number;
          final: number;
          adjusted: number;
          insurance: number;
        }
      >(),
    ),
  )
    .map(
      ([providerName, totals]) =>
        `${providerName} - Original: ${currencyFormatter.format(
          totals.original,
        )} Final: ${currencyFormatter.format(totals.final)} Adjusted: ${currencyFormatter.format(totals.adjusted)} Insurance: ${currencyFormatter.format(totals.insurance)}`,
    )
    .join(multilineSeparator);
  const allHealthInsOriginalFinalAdjusted = Array.from(
    matter.medicalTreatments.reduce(
      (acc, treatment) => {
        const providerConnection = getMedicalTreatmentContactConnection(
          treatment,
          "Provider",
        );
        const providerName =
          getConnectionContactName(providerConnection?.contact) ||
          treatment.description?.trim() ||
          "";

        const trimmedProviderName = providerName.trim();
        if (!trimmedProviderName) {
          return acc;
        }

        const bills = matter.medicalTreatmentBills.filter(
          (bill) => bill.medicalTreatmentId === treatment.id,
        );

        const existing = acc.get(trimmedProviderName) ?? {
          original: 0,
          final: 0,
          adjusted: 0,
        };

        const totals = bills.reduce(
          (billAcc, bill) => ({
            original: billAcc.original + Number(bill.originalBill ?? 0),
            final:
              billAcc.final +
              Number(
                bill.negotiatedAmount ??
                  bill.adjustedBill ??
                  bill.originalBill ??
                  0,
              ),
            adjusted: billAcc.adjusted + Number(bill.adjustedBill ?? 0),
          }),
          existing,
        );

        acc.set(trimmedProviderName, totals);
        return acc;
      },
      new Map<
        string,
        {
          original: number;
          final: number;
          adjusted: number;
        }
      >(),
    ),
  )
    .map(
      ([providerName, totals]) =>
        `${providerName} - Original: ${currencyFormatter.format(
          totals.original,
        )} Final: ${currencyFormatter.format(totals.final)} Adjusted: ${currencyFormatter.format(totals.adjusted)}`,
    )
    .join(multilineSeparator);
  const allMedicalBillTotals = calculateMedicalTreatmentBillTotals(
    matter.medicalTreatmentBills,
  );
  const expenseTotals = calculateExpenseTotals(matter.expenses);
  const liens = matter.liens as unknown as MatterLienV2[];
  const totalLiens = liens.reduce(
    (total, lien) =>
      total +
      Number(
        lien.negotiatedAmount ?? lien.originalAmount ?? lien.paidAmount ?? 0,
      ),
    0,
  );
  const defendantContactIds = new Set(
    getMatterContactConnections(matter)
      .filter((connection) => connection.label === "Defendant")
      .map((connection) => connection.contactId)
      .filter((contactId): contactId is string => Boolean(contactId)),
  );
  const isDefendantInsurance = (insuranceId?: string) => {
    if (!insuranceId) {
      return false;
    }

    const insurance = matter.insurances.find(
      (candidate) => candidate.id === insuranceId,
    );
    return insurance
      ? [
          ...getInsuranceContactConnections(insurance, "Insured"),
          ...getInsuranceContactConnections(insurance, "Policy Holder"),
        ].some((connection) =>
          defendantContactIds.has(connection.targetObjectId),
        )
      : false;
  };
  const isMedPaySettlementOffer = (
    offer: Matter["settlementOffers"][number],
  ) =>
    offer.coverageType
      ? offer.coverageType === "MedPay"
      : (offer.medPay ?? false);
  const settlementTotals = calculateSettlementTotals(
    matter.settlementOffers.map((offer) => ({
      amount: Number(offer.amount ?? 0),
      accepted: Boolean(offer.accepted),
      attorneyFeePercentage: Number(offer.attorneyFeePercentage ?? 0),
      medPay: isMedPaySettlementOffer(offer),
      medPayRefund: Boolean(offer.medPayRefund),
      medPayRefundAmount: Number(offer.medPayRefundAmount ?? 0),
    })),
  );
  const totalFees =
    matter.settlementOffers
      .filter((offer) => offer.accepted)
      .reduce(
        (total, offer) => total + Number(offer.attorneyFeeAmount ?? 0),
        0,
      ) +
    matter.settlementCounters
      .filter((counter) => counter.accepted)
      .reduce(
        (total, counter) => total + Number(counter.attorneyFeeAmount ?? 0),
        0,
      ) +
    liens
      .filter(
        (lien) =>
          (lien.category ?? lien.type ?? "").trim().toLowerCase() ===
          "attorney",
      )
      .reduce(
        (total, lien) =>
          total + Number(lien.negotiatedAmount ?? lien.originalAmount ?? 0),
        0,
      );
  const totalHealthInsuranceOriginal = liens
    .filter((lien) => {
      const lienType = (lien.type ?? "").trim().toLowerCase();
      const lienCategory = (lien.category ?? "").trim().toLowerCase();

      return lienType === "medical" || lienCategory === "medical";
    })
    .reduce((total, lien) => total + Number(lien.originalAmount ?? 0), 0);
  const totalMedpay = matter.settlementOffers
    .filter((offer) => offer.accepted && isMedPaySettlementOffer(offer))
    .reduce(
      (total, offer) =>
        total +
        calculateNetSettlementAmount(
          Number(offer.amount ?? 0),
          true,
          Boolean(offer.medPayRefund),
          Number(offer.medPayRefundAmount ?? 0),
        ),
      0,
    );
  const totalSettlement =
    settlementTotals.totalAcceptedSettlements +
    matter.settlementCounters
      .filter((counter) => counter.accepted)
      .reduce((total, counter) => total + Number(counter.amount ?? 0), 0);
  const netToFirm =
    settlementTotals.netToFirm +
    matter.settlementCounters
      .filter((counter) => counter.accepted)
      .reduce(
        (total, counter) =>
          total +
          calculateAttorneyFee(
            counter.amount ?? 0,
            counter.attorneyFeePercentage,
          ),
        0,
      );
  const netClientIssued = calculateNetToClient(
    totalSettlement,
    totalLiens + expenseTotals.totalExpenses + allMedicalBillTotals.totalBill,
    netToFirm,
  );
  const totalOriginalBills = matter.medicalTreatmentBills.reduce(
    (total, bill) => total + Number(bill.originalBill ?? 0),
    0,
  );
  const totalThirdParty =
    matter.settlementOffers
      .filter(
        (offer) =>
          offer.accepted &&
          isDefendantInsurance(offer.insuranceId) &&
          !isMedPaySettlementOffer(offer) &&
          !["UM", "UIM"].includes(offer.coverageType ?? ""),
      )
      .reduce((total, offer) => total + Number(offer.amount ?? 0), 0) +
    matter.settlementCounters
      .filter(
        (counter) =>
          counter.accepted &&
          isDefendantInsurance(counter.insuranceId) &&
          !["MedPay", "UM", "UIM"].includes(counter.coverageType ?? ""),
      )
      .reduce((total, counter) => total + Number(counter.amount ?? 0), 0);
  const totalUmUim = matter.settlementOffers
    .filter(
      (offer) =>
        offer.accepted && ["UM", "UIM"].includes(offer.coverageType ?? ""),
    )
    .reduce((total, offer) => total + Number(offer.amount ?? 0), 0);
  const baseOptions: TemplateDictionaryOptionsMap = {
    // #region Date
    today: [
      {
        value: formatDate(dayjs().toString()) ?? "",
        label: formatDate(dayjs().toString()) ?? "",
        context: "",
      },
    ],
    dateOfLoss: [
      {
        value: formatDate(matter.dateOfLoss) ?? "",
        label: formatDate(matter.dateOfLoss) ?? "",
        context: "",
      },
    ],
    // #endregion
    // #region Firm
    firmName: [
      {
        value: firm?.name ?? "",
        label: firm?.name ?? "",
        context: "",
      },
    ],
    firmOfficeAddressLine1:
      firmOffices?.map((office) => {
        const formatted = formatAddressLine1({
          street1: office.street1,
          street2: office.street2 ?? undefined,
        });
        return {
          value: formatted,
          label: formatted,
          context: office.label,
        };
      }) ?? [],
    firmOfficeAddressLine2:
      firmOffices?.map((office) => {
        const formatted = formatAddressLine2({
          city: office.city,
          state: office.state,
          zipCode: office.zipCode,
        });
        return {
          value: formatted,
          label: formatted,
          context: office.label,
        };
      }) ?? [],
    firmOfficeAddress:
      firmOffices?.map((office) => {
        const formatted = formatAddress({
          street1: office.street1,
          street2: office.street2 ?? undefined,
          city: office.city,
          state: office.state,
          zipCode: office.zipCode,
          country: office.country,
        });
        return {
          value: formatted,
          label: formatted,
          context: office.label,
        };
      }) ?? [],
    firmOfficePhoneNumber:
      firmOffices
        ?.filter((office) => office.phoneNumber)
        .map((office) => ({
          value: formatLocalOrIntlPhoneNumber({
            number: office.phoneNumber,
          }),
          label: formatLocalOrIntlPhoneNumber({
            number: office.phoneNumber,
          }),
          context: office.label,
        })) ?? [],
    // #endregion
    // #region Matter

    matterTitle: [
      {
        value: matter.title,
        label: matter.title,
        context: "",
      },
    ],
    matterType: [
      {
        value: matter.incidentType?.label ?? "",
        label: matter.incidentType?.label ?? "",
        context: "",
      },
    ],
    incidentType: [
      {
        value: matter.incidentType?.label ?? "",
        label: matter.incidentType?.label ?? "",
        context: "",
      },
    ],
    matterStatus: [
      {
        value: matter.status.label,
        label: matter.status.label,
        context: "",
      },
    ],
    matterDateRetained: [
      {
        value: formatDate(matter.dateRetained) ?? "",
        label: formatDate(matter.dateRetained) ?? "",
        context: "",
      },
    ],
    matterFiledDate: [
      {
        value: formatDate(matter.filedDate) ?? "",
        label: formatDate(matter.filedDate) ?? "",
        context: "",
      },
    ],
    matterClientInjuryDescription: matter.clientInjuryDescription?.trim()
      ? [
          {
            value: matter.clientInjuryDescription.trim(),
            label: matter.clientInjuryDescription.trim(),
            context: "",
          },
        ]
      : [],
    incidentAddressLine1: [
      {
        value: formatAddressLine1({
          street1: matter.incidentAddressStreet1 ?? undefined,
          street2: matter.incidentAddressStreet2 ?? undefined,
        }),
        label: formatAddressLine1({
          street1: matter.incidentAddressStreet1 ?? undefined,
          street2: matter.incidentAddressStreet2 ?? undefined,
        }),
        context: incidentAddress,
      },
    ],
    incidentAddressLine2: [
      {
        value: formatAddressLine2({
          city: matter.incidentAddressCity ?? undefined,
          state: matter.incidentAddressState ?? undefined,
          zipCode: matter.incidentAddressZip ?? undefined,
        }),
        label: formatAddressLine2({
          city: matter.incidentAddressCity ?? undefined,
          state: matter.incidentAddressState ?? undefined,
          zipCode: matter.incidentAddressZip ?? undefined,
        }),
        context: incidentAddress,
      },
    ],
    incidentAddressStreet1: [
      {
        value: matter.incidentAddressStreet1 ?? "",
        label: matter.incidentAddressStreet1 ?? "",
        context: incidentAddress,
      },
    ],
    incidentAddressStreet2: [
      {
        value: matter.incidentAddressStreet2 ?? "",
        label: matter.incidentAddressStreet2 ?? "",
        context: incidentAddress,
      },
    ],
    incidentAddressCity: [
      {
        value: matter.incidentAddressCity ?? "",
        label: matter.incidentAddressCity ?? "",
        context: incidentAddress,
      },
    ],
    incidentAddressState: [
      {
        value: matter.incidentAddressState ?? "",
        label: matter.incidentAddressState ?? "",
        context: incidentAddress,
      },
    ],
    incidentAddressZipCode: [
      {
        value: matter.incidentAddressZip ?? "",
        label: matter.incidentAddressZip ?? "",
        context: incidentAddress,
      },
    ],
    incidentAddressCountry: [
      {
        value: matter.incidentAddressCountry ?? "",
        label: matter.incidentAddressCountry ?? "",
        context: incidentAddress,
      },
    ],

    incidentLocation: [
      {
        value: formatAddress({
          street1: matter.incidentAddressStreet1,
          street2: matter.incidentAddressStreet2,
          city: matter.incidentAddressCity,
          state: matter.incidentAddressState ?? "",
          zipCode: matter.incidentAddressZip,
          country: undefined,
        }),
        label: formatAddress({
          street1: matter.incidentAddressStreet1,
          street2: matter.incidentAddressStreet2,
          city: matter.incidentAddressCity,
          state: matter.incidentAddressState ?? "",
          zipCode: matter.incidentAddressZip,
          country: undefined,
        }),
        context: "",
      },
    ],
    incidentAddressDetails: [
      {
        value: matter.incidentAddressDetails ?? "",
        label: matter.incidentAddressDetails ?? "",
        context: "",
      },
    ],
    incidentCounty: [
      {
        value: matter.incidentAddressCounty ?? "",
        label: matter.incidentAddressCounty ?? "",
        context: "",
      },
    ],
    incidentTime: [
      {
        value: formatTime(matter.incidentTime ?? undefined) ?? "",
        label: formatTime(matter.incidentTime ?? undefined) ?? "",
        context: "",
      },
    ],
    // #endregion
    // #region Firm User
    ...userOptions,
    // #endregion
    // #region Contact
    contactTitle: uniqBy(contactsWithContact, getContactRoleKey)
      .map((connection) => ({
        connection,
        title:
          connection.contact.individualTitle ?? connection.contact.title ?? "",
      }))
      .filter(({ title }) => Boolean(title))
      .map(({ connection, title }) => ({
        id: getContactOptionId(connection, "contactTitle"),
        value: title,
        label: title,
        context: `${formatContactsName(connection.contact) ?? ""} - ${connection.label}`,
      })),
    contactName: uniqBy(contactsWithContact, getContactRoleKey).map(
      (connection) => ({
        id: getContactOptionId(connection, "contactName"),
        value: formatContactsName(connection.contact) ?? "",
        label: formatContactsName(connection.contact) ?? "",
        context: connection.label,
      }),
    ),
    contactFirstName: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualFirstName)
      .map((connection) => ({
        id: getContactOptionId(connection, "contactFirstName"),
        value: connection.contact.individualFirstName ?? "",
        label: connection.contact.individualFirstName ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      })),
    contactLastName: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualLastName)
      .map((connection) => ({
        id: getContactOptionId(connection, "contactLastName"),
        value: connection.contact.individualLastName ?? "",
        label: connection.contact.individualLastName ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      })),
    contactMiddleName: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualMiddleName)
      .map((connection) => ({
        id: getContactOptionId(connection, "contactMiddleName"),
        value: connection.contact.individualMiddleName ?? "",
        label: connection.contact.individualMiddleName ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      })),
    contactEmail: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getContactCollection(connection.contact.emails).map((email) => ({
          id: getContactOptionId(connection, "contactEmail", email.id),
          value: email.address ?? "",
          label: email.address ?? "",
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${email.label || ""}${email.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactPhone: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getContactCollection(connection.contact.phones).map((phone) => ({
          id: getContactOptionId(connection, "contactPhone", phone.id),
          value: formatLocalOrIntlPhoneNumber(phone),
          label: formatLocalOrIntlPhoneNumber(phone),
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${phone.label || ""}${phone.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactDateOfBirth: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualDateOfBirth)
      .map((connection) => {
        const dob = connection.contact.individualDateOfBirth;
        const formatted = formatDate(dob as string | Date) ?? "";
        return {
          id: getContactOptionId(connection, "contactDateOfBirth"),
          value: formatted,
          label: formatted,
          context: `${formatContactsName(connection.contact)} - ${connection.label}`,
        };
      }),
    contactPronoun: uniqBy(contactsWithContact, getContactRoleKey).map(
      (connection) => {
        const gender = connection.contact.individualGender;
        const pronoun =
          gender === "Male" ? "he" : gender === "Female" ? "she" : "they";
        return {
          id: getContactOptionId(connection, "contactPronoun"),
          value: pronoun,
          label: pronoun,
          context: `${formatContactsName(connection.contact)} - ${connection.label}`,
        };
      },
    ),
    contactPossessivePronoun: uniqBy(
      contactsWithContact,
      getContactRoleKey,
    ).map((connection) => {
      const gender = connection.contact.individualGender;
      const pronoun =
        gender === "Male" ? "his" : gender === "Female" ? "her" : "their";
      return {
        id: getContactOptionId(connection, "contactPossessivePronoun"),
        value: pronoun,
        label: pronoun,
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      };
    }),
    contactObjectivePronoun: uniqBy(contactsWithContact, getContactRoleKey).map(
      (connection) => {
        const gender = connection.contact.individualGender;
        const pronoun =
          gender === "Male" ? "him" : gender === "Female" ? "her" : "them";
        return {
          id: getContactOptionId(connection, "contactObjectivePronoun"),
          value: pronoun,
          label: pronoun,
          context: `${formatContactsName(connection.contact)} - ${connection.label}`,
        };
      },
    ),
    contactFax: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getContactCollection(connection.contact.faxes).map((fax) => ({
          id: getContactOptionId(connection, "contactFax", fax.id),
          value: formatLocalOrIntlPhoneNumber(fax),
          label: formatLocalOrIntlPhoneNumber(fax),
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${fax.label || ""}${fax.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactAddressLine1: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getConnectionAddresses(connection).map((address) => {
          const formatted = formatAddressLine1({
            street1: address.street1 ?? undefined,
            street2: address.street2 ?? undefined,
          });
          return {
            id: getContactOptionId(
              connection,
              "contactAddressLine1",
              address.id,
            ),
            value: formatted,
            label: formatted,
            context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
          };
        }),
    ),
    contactAddressLine2: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getConnectionAddresses(connection).map((address) => {
          const formatted = formatAddressLine2({
            city: address.city ?? undefined,
            state: address.state ?? undefined,
            zipCode: address.zipCode ?? undefined,
          });
          return {
            id: getContactOptionId(
              connection,
              "contactAddressLine2",
              address.id,
            ),
            value: formatted,
            label: formatted,
            context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
          };
        }),
    ),
    contactAddress: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getConnectionAddresses(connection).map((address) => {
          const formatted = formatAddress({
            street1: address.street1 ?? undefined,
            street2: address.street2 ?? undefined,
            city: address.city ?? undefined,
            state: address.state ?? "",
            zipCode: address.zipCode ?? undefined,
            country: address.country ?? undefined,
          });
          return {
            id: getContactOptionId(connection, "contactAddress", address.id),
            value: formatted,
            label: formatted,
            context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
          };
        }),
    ),
    contactAddressStreet1: uniqBy(
      contactsWithContact,
      getContactRoleKey,
    ).flatMap((connection) =>
      getConnectionAddresses(connection).map((address) => ({
        id: getContactOptionId(connection, "contactAddressStreet1", address.id),
        value: address.street1 ?? "",
        label: address.street1 ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
      })),
    ),
    contactAddressStreet2: uniqBy(
      contactsWithContact,
      getContactRoleKey,
    ).flatMap((connection) =>
      getConnectionAddresses(connection)
        .filter((address) => address.street2)
        .map((address) => ({
          id: getContactOptionId(
            connection,
            "contactAddressStreet2",
            address.id,
          ),
          value: address.street2 ?? "",
          label: address.street2 ?? "",
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactAddressCity: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getConnectionAddresses(connection).map((address) => ({
          id: getContactOptionId(connection, "contactAddressCity", address.id),
          value: address.city ?? "",
          label: address.city ?? "",
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactAddressState: uniqBy(contactsWithContact, getContactRoleKey).flatMap(
      (connection) =>
        getConnectionAddresses(connection).map((address) => ({
          id: getContactOptionId(connection, "contactAddressState", address.id),
          value: address.state ?? "",
          label: address.state ?? "",
          context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
        })),
    ),
    contactAddressZipCode: uniqBy(
      contactsWithContact,
      getContactRoleKey,
    ).flatMap((connection) =>
      getConnectionAddresses(connection).map((address) => ({
        id: getContactOptionId(connection, "contactAddressZipCode", address.id),
        value: address.zipCode ?? "",
        label: address.zipCode ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
      })),
    ),
    contactAddressCountry: uniqBy(
      contactsWithContact,
      getContactRoleKey,
    ).flatMap((connection) =>
      getConnectionAddresses(connection).map((address) => ({
        id: getContactOptionId(connection, "contactAddressCountry", address.id),
        value: address.country ?? "",
        label: address.country ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label} - ${address.label || ""}${address.isPrimary ? " - Primary" : ""}`,
      })),
    ),
    contactSSN: uniqBy(contactsWithContact, getContactRoleKey).map(
      (connection) => ({
        id: getContactOptionId(connection, "contactSSN"),
        value: connection.contact.individualSocialSecurityNumber ?? "",
        label: connection.contact.individualSocialSecurityNumber ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      }),
    ),
    contactDriverLicenseNumber: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualDriverLicenseNumber)
      .map((connection) => ({
        id: getContactOptionId(connection, "contactDriverLicenseNumber"),
        value: connection.contact.individualDriverLicenseNumber ?? "",
        label: connection.contact.individualDriverLicenseNumber ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      })),
    contactDriverLicenseState: uniqBy(contactsWithContact, getContactRoleKey)
      .filter((connection) => connection.contact.individualDriverLicenseState)
      .map((connection) => ({
        id: getContactOptionId(connection, "contactDriverLicenseState"),
        value: connection.contact.individualDriverLicenseState ?? "",
        label: connection.contact.individualDriverLicenseState ?? "",
        context: `${formatContactsName(connection.contact)} - ${connection.label}`,
      })),
    // #endregion
    // #region Defendant
    allDefendants: allDefendants
      ? [
          {
            value: allDefendants,
            label: allDefendants,
            context: "All defendants",
          },
        ]
      : [],
    allHealthProviders: allHealthProviders
      ? [
          {
            value: allHealthProviders,
            label: allHealthProviders,
            context: "All health providers",
          },
        ]
      : [],
    allProvidersDates: allProvidersDates
      ? [
          {
            value: allProvidersDates,
            label: allProvidersDates,
            context: "All providers dates",
          },
        ]
      : [],
    allProvidersFinal: allProvidersFinal
      ? [
          {
            value: allProvidersFinal,
            label: allProvidersFinal,
            context: "All providers final",
          },
        ]
      : [],
    allProvidersOriginal: allProvidersOriginal
      ? [
          {
            value: allProvidersOriginal,
            label: allProvidersOriginal,
            context: "All providers original",
          },
        ]
      : [],
    allProvidersOriginalFinal: allProvidersOriginalFinal
      ? [
          {
            value: allProvidersOriginalFinal,
            label: allProvidersOriginalFinal,
            context: "All providers original final",
          },
        ]
      : [],
    allProvidersOrigFinalAdjustedInsPaid: allProvidersOrigFinalAdjustedInsPaid
      ? [
          {
            value: allProvidersOrigFinalAdjustedInsPaid,
            label: allProvidersOrigFinalAdjustedInsPaid,
            context: "All providers original final adjusted insurance paid",
          },
        ]
      : [],
    allHealthInsOriginalFinalAdjusted: allHealthInsOriginalFinalAdjusted
      ? [
          {
            value: allHealthInsOriginalFinalAdjusted,
            label: allHealthInsOriginalFinalAdjusted,
            context: "All health insurances",
          },
        ]
      : [],
    totalCosts: [
      {
        value: currencyFormatter.format(expenseTotals.totalExpenses),
        label: currencyFormatter.format(expenseTotals.totalExpenses),
        context: "",
      },
    ],
    totalFees: [
      {
        value: currencyFormatter.format(totalFees),
        label: currencyFormatter.format(totalFees),
        context: "",
      },
    ],
    totalHealthInsuranceOriginal: [
      {
        value: currencyFormatter.format(totalHealthInsuranceOriginal),
        label: currencyFormatter.format(totalHealthInsuranceOriginal),
        context: "",
      },
    ],
    totalMedpay: [
      {
        value: currencyFormatter.format(totalMedpay),
        label: currencyFormatter.format(totalMedpay),
        context: "",
      },
    ],
    netClientIssued: [
      {
        value: currencyFormatter.format(netClientIssued),
        label: currencyFormatter.format(netClientIssued),
        context: "",
      },
    ],
    totalFinalBills: [
      {
        value: currencyFormatter.format(allMedicalBillTotals.totalBill),
        label: currencyFormatter.format(allMedicalBillTotals.totalBill),
        context: "",
      },
    ],
    totalOriginalBills: [
      {
        value: currencyFormatter.format(totalOriginalBills),
        label: currencyFormatter.format(totalOriginalBills),
        context: "",
      },
    ],
    totalSettlement: [
      {
        value: currencyFormatter.format(totalSettlement),
        label: currencyFormatter.format(totalSettlement),
        context: "",
      },
    ],
    totalStillOwed: [
      {
        value: currencyFormatter.format(allMedicalBillTotals.totalOutstanding),
        label: currencyFormatter.format(allMedicalBillTotals.totalOutstanding),
        context: "",
      },
    ],
    totalThirdParty: [
      {
        value: currencyFormatter.format(totalThirdParty),
        label: currencyFormatter.format(totalThirdParty),
        context: "",
      },
    ],
    totalUmUim: [
      {
        value: currencyFormatter.format(totalUmUim),
        label: currencyFormatter.format(totalUmUim),
        context: "",
      },
    ],
    // #endregion
    ...buildVehicleOwnerOptions(
      "clientVehicle",
      clientVehicles,
      vehicleOwnerContacts,
    ),
    ...buildVehicleOwnerOptions(
      "defendantVehicle",
      defendantVehicles,
      vehicleOwnerContacts,
    ),
    // #region Client Vehicle
    clientVehicleYear: clientVehicles
      .filter((vehicle) => vehicle.year)
      .map((vehicle) => ({
        value: String(vehicle.year ?? ""),
        label: String(vehicle.year ?? ""),
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicleMake: clientVehicles
      .filter((vehicle) => vehicle.make.trim())
      .map((vehicle) => ({
        value: vehicle.make,
        label: vehicle.make,
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicleModel: clientVehicles
      .filter((vehicle) => vehicle.model)
      .map((vehicle) => ({
        value: vehicle.model ?? "",
        label: vehicle.model ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicleColor: clientVehicles.flatMap((vehicle) => {
      const color = vehicle.color;
      if (!color?.trim()) return [];
      return [
        {
          value: color,
          label: color,
          context: formatVehicleTitle(vehicle),
        },
      ];
    }),
    clientVehicleLicensePlateNumber: clientVehicles
      .filter((vehicle) => vehicle.licensePlateNumber?.trim())
      .map((vehicle) => ({
        value: vehicle.licensePlateNumber ?? "",
        label: vehicle.licensePlateNumber ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicleRegistrationState: clientVehicles
      .filter((vehicle) => vehicle.registrationState?.trim())
      .map((vehicle) => ({
        value: vehicle.registrationState ?? "",
        label: vehicle.registrationState ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicleVin: clientVehicles
      .filter((vehicle) => vehicle.vin?.trim())
      .map((vehicle) => ({
        value: vehicle.vin ?? "",
        label: vehicle.vin ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    clientVehicle: clientVehicles.map((vehicle) => ({
      value: formatVehicleTitle(vehicle),
      label: formatVehicleTitle(vehicle),
      context: vehicle.vin ? `VIN: ${vehicle.vin}` : "",
    })),
    // #endregion
    // #region Defendant Vehicle
    defendantVehicleYear: defendantVehicles
      .filter((vehicle) => vehicle.year)
      .map((vehicle) => ({
        value: String(vehicle.year ?? ""),
        label: String(vehicle.year ?? ""),
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicleMake: defendantVehicles
      .filter((vehicle) => vehicle.make.trim())
      .map((vehicle) => ({
        value: vehicle.make,
        label: vehicle.make,
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicleModel: defendantVehicles
      .filter((vehicle) => vehicle.model)
      .map((vehicle) => ({
        value: vehicle.model ?? "",
        label: vehicle.model ?? "<No Model>",
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicleColor: defendantVehicles.flatMap((vehicle) => {
      const color = vehicle.color;
      if (!color?.trim()) return [];
      return [
        {
          value: color,
          label: color,
          context: formatVehicleTitle(vehicle),
        },
      ];
    }),
    defendantVehicleLicensePlateNumber: defendantVehicles
      .filter((vehicle) => vehicle.licensePlateNumber?.trim())
      .map((vehicle) => ({
        value: vehicle.licensePlateNumber ?? "",
        label: vehicle.licensePlateNumber ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicleRegistrationState: defendantVehicles
      .filter((vehicle) => vehicle.registrationState?.trim())
      .map((vehicle) => ({
        value: vehicle.registrationState ?? "",
        label: vehicle.registrationState ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicleVin: defendantVehicles
      .filter((vehicle) => vehicle.vin?.trim())
      .map((vehicle) => ({
        value: vehicle.vin ?? "",
        label: vehicle.vin ?? "",
        context: formatVehicleTitle(vehicle),
      })),
    defendantVehicle: defendantVehicles.map((vehicle) => ({
      value: formatVehicleTitle(vehicle),
      label: formatVehicleTitle(vehicle),
      context: vehicle.vin ? `VIN: ${vehicle.vin}` : "",
    })),
    // #endregion
    // #region Insurance
    clientInsurancePolicyNumber: clientInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "PolicyNumber"),
      value: ins.policyNumber ?? "<No Policy Number>",
      label: ins.policyNumber ?? "<No Policy Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    clientInsuranceClaimNumber: clientInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "ClaimNumber"),
      value: ins.claimNumber ?? "<No Claim Number>",
      label: ins.claimNumber ?? "<No Claim Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    clientInsuranceGroupNumber: clientInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "GroupNumber"),
      value: ins.groupNumber ?? "<No Group Number>",
      label: ins.groupNumber ?? "<No Group Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    clientInsuranceMemberNumber: clientInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "MemberNumber"),
      value: ins.memberNumber ?? "<No Member Number>",
      label: ins.memberNumber ?? "<No Member Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    clientInsurancePolicyLimits: clientInsurance
      .filter((ins) => ins.policyLimits)
      .map((ins) => ({
        id: getInsuranceOptionId(ins.id, "PolicyLimits"),
        value: ins.policyLimits ?? "",
        label: ins.policyLimits ?? "<No Policy Limits>",
        context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
      })),
    defendantInsurancePolicyNumber: defendantInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "PolicyNumber"),
      value: ins.policyNumber ?? "<No Policy Number>",
      label: ins.policyNumber ?? "<No Policy Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    defendantInsuranceClaimNumber: defendantInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "ClaimNumber"),
      value: ins.claimNumber ?? "<No Claim Number>",
      label: ins.claimNumber ?? "<No Claim Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    defendantInsuranceGroupNumber: defendantInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "GroupNumber"),
      value: ins.groupNumber ?? "<No Group Number>",
      label: ins.groupNumber ?? "<No Group Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    defendantInsuranceMemberNumber: defendantInsurance.map((ins) => ({
      id: getInsuranceOptionId(ins.id, "MemberNumber"),
      value: ins.memberNumber ?? "<No Member Number>",
      label: ins.memberNumber ?? "<No Member Number>",
      context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
    })),
    defendantInsurancePolicyLimits: defendantInsurance
      .filter((ins) => ins.policyLimits)
      .map((ins) => ({
        id: getInsuranceOptionId(ins.id, "PolicyLimits"),
        value: ins.policyLimits ?? "",
        label: ins.policyLimits ?? "<No Policy Limits>",
        context: `${formatContactsName(ins.insured?.contact) || "<No Insured>"} - ${ins.type || "<No Type>"}`,
      })),
    clientInsuranceSubrogationFileNumber: clientInsurance
      .filter((ins) => ins.subrogation)
      .map((ins) => ({
        id: getInsuranceOptionId(ins.id, "SubrogationFileNumber"),
        value: ins.subrogationFileNumber ?? "",
        label: ins.subrogationFileNumber ?? "<No File Number>",
        context: `${formatContactsName(ins.subrogationVendor?.contact) || "<No Vendor>"} - ${ins.type || "<No Type>"}`,
      })),
    defendantInsuranceSubrogationFileNumber: defendantInsurance
      .filter((ins) => ins.subrogation)
      .map((ins) => ({
        id: getInsuranceOptionId(ins.id, "SubrogationFileNumber"),
        value: ins.subrogationFileNumber ?? "",
        label: ins.subrogationFileNumber ?? "<No File Number>",
        context: `${formatContactsName(ins.subrogationVendor?.contact) || "<No Vendor>"} - ${ins.type || "<No Type>"}`,
      })),
    // #endregion
  };

  return {
    ...baseOptions,
    ...buildInsuranceContactOptionsMap({
      clientInsurance,
      defendantInsurance,
    }),
  };
};

function buildInsuranceContactOptionsMap({
  clientInsurance,
  defendantInsurance,
}: {
  clientInsurance: InsuranceWithConnections[];
  defendantInsurance: InsuranceWithConnections[];
}): TemplateDictionaryOptionsMap {
  if (!insuranceContactVariableMetadata.length) return {};

  const result: TemplateDictionaryOptionsMap = {};
  const collections: Record<
    InsuranceVariablePrefix,
    InsuranceWithConnections[]
  > = {
    clientInsurance,
    defendantInsurance,
  };

  insuranceContactVariableMetadata.forEach((metadata) => {
    const collection = collections[metadata.prefix];
    const options = buildInsuranceContactFieldOptions(
      collection,
      metadata.contactRole,
      metadata.field,
    );
    result[metadata.variable] = options;
  });

  return result;
}

function buildInsuranceContactFieldOptions(
  insuranceList: InsuranceWithConnections[],
  contactRole: InsuranceContactRole,
  field: string,
): TemplateDictionaryOption[] {
  if (!insuranceList.length) return [];

  const roleProperty = INSURANCE_ROLE_PROPERTY_MAP[contactRole];
  const roleLabel = startCase(contactRole);
  const fallbackName = `<No ${roleLabel}>`;

  return insuranceList.flatMap((insurance) => {
    const connection = insurance[roleProperty] as
      | InsuranceConnectionWithContact
      | undefined;
    return buildInsuranceConnectionFieldOptions({
      connection,
      field,
      fallbackName,
      insurance,
      roleLabel,
    });
  });
}

function buildInsuranceConnectionFieldOptions({
  connection,
  field,
  fallbackName,
  insurance,
  roleLabel,
}: {
  connection?: InsuranceConnectionWithContact;
  field: string;
  fallbackName: string;
  insurance: InsuranceWithConnections;
  roleLabel: string;
}): TemplateDictionaryOption[] {
  const contactName = getContactDisplayName(connection, fallbackName);
  const insuranceType = insurance.type || "<No Type>";

  const makeOption = (
    value: string,
    extraParts: Array<string | undefined | null | false> = [],
    channelId?: string,
  ): TemplateDictionaryOption => ({
    id: getInsuranceOptionId(insurance.id, field, roleLabel, channelId),
    value,
    label: value,
    context: buildInsuranceContext({
      contactName,
      roleLabel,
      insuranceType,
      extraParts,
    }),
  });

  switch (field) {
    case "":
    case "Name":
      return [makeOption(contactName)];
    case "FirstName": {
      const raw = connection?.contact.individualFirstName;
      const firstName = typeof raw === "string" ? raw.trim() : "";
      if (!firstName) return [];
      return [makeOption(firstName)];
    }
    case "LastName": {
      const raw = connection?.contact.individualLastName;
      const lastName = typeof raw === "string" ? raw.trim() : "";
      if (!lastName) return [];
      return [makeOption(lastName)];
    }
    case "Email": {
      const emails = connection?.contact.emails ?? [];
      return emails
        .filter((email) => email.address)
        .map((email) =>
          makeOption(
            email.address ?? "",
            [email.label, email.isPrimary ? "Primary" : undefined],
            email.id,
          ),
        );
    }
    case "Phone": {
      const phones = connection?.contact.phones ?? [];
      return phones
        .map((phone) => {
          const formatted = formatLocalOrIntlPhoneNumber(phone).trim();
          if (!formatted) return undefined;
          return makeOption(
            formatted,
            [phone.label, phone.isPrimary ? "Primary" : undefined],
            phone.id,
          );
        })
        .filter(
          (option): option is TemplateDictionaryOption => option !== undefined,
        );
    }
    case "Fax": {
      const faxes = connection?.contact.faxes ?? [];
      return faxes
        .map((fax) => {
          const formatted = formatLocalOrIntlPhoneNumber(fax).trim();
          if (!formatted) return undefined;
          return makeOption(
            formatted,
            [fax.label, fax.isPrimary ? "Primary" : undefined],
            fax.id,
          );
        })
        .filter(
          (option): option is TemplateDictionaryOption => option !== undefined,
        );
    }
    case "AddressLine1": {
      const addresses = connection?.contact.addresses ?? [];
      return addresses
        .map((address) => {
          const formatted = formatAddressLine1({
            street1: address.street1 ?? undefined,
            street2: address.street2 ?? undefined,
          }).trim();
          if (!formatted) return undefined;
          return makeOption(
            formatted,
            [address.label, address.isPrimary ? "Primary" : undefined],
            address.id,
          );
        })
        .filter(
          (option): option is TemplateDictionaryOption => option !== undefined,
        );
    }
    case "AddressLine2": {
      const addresses = connection?.contact.addresses ?? [];
      return addresses
        .map((address) => {
          const formatted = formatAddressLine2({
            city: address.city ?? undefined,
            state: address.state ?? undefined,
            zipCode: address.zipCode ?? undefined,
          }).trim();
          if (!formatted) return undefined;
          return makeOption(
            formatted,
            [address.label, address.isPrimary ? "Primary" : undefined],
            address.id,
          );
        })
        .filter(
          (option): option is TemplateDictionaryOption => option !== undefined,
        );
    }
    default:
      return [];
  }
}

function buildInsuranceContext({
  contactName,
  roleLabel,
  insuranceType,
  extraParts = [],
}: {
  contactName: string;
  roleLabel: string;
  insuranceType: string;
  extraParts?: Array<string | undefined | null | false>;
}): string {
  const cleanedExtras = extraParts
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0);
  const typePart = insuranceType.trim() ? insuranceType : "<No Type>";
  return [contactName, roleLabel, ...cleanedExtras, typePart]
    .filter((part) => part && part.length > 0)
    .join(" - ");
}

function getContactDisplayName(
  connection:
    | ContactConnectionWithContact
    | InsuranceConnectionWithContact
    | undefined,
  fallback: string,
): string {
  const formatted = formatContactsName(connection?.contact) ?? "";
  const trimmed = formatted.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}
